import cx from "classnames";
import Image from "next/image";
import React, { useMemo } from "react";
import { ButtonLink } from "components/common/Button";
import CourseBadge from "components/common/CourseBadge";
import CreditHoursBadge from "components/common/CreditHoursBadge/CreditHoursBadge";
import { ProductBundlesQueryNodes as ProductBundle } from "components/courses/Catalog/__generated__/ProductBundles";
import { mapNodes } from "helpers/mapNodes";
import { formatUSD } from "helpers/strings";
import { Optional } from "helpers/types";
import {
  PracticeToolsProductsNode as ProductType,
  PracticeToolsProductsVariableProductInlineFragment as VariableProduct,
} from "./__generated__/queries";
import styles from "./ProductCard.module.css";

const isProductType = (variableToCheck: unknown): variableToCheck is ProductType => {
  return (
    typeof variableToCheck === "object" &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (variableToCheck as any)?.image !== "undefined"
  );
};

const isVariableProduct = (variableToCheck: unknown): variableToCheck is VariableProduct => {
  return (
    isProductType(variableToCheck) &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (variableToCheck as any)?.variations !== "undefined"
  );
};

const isCourseGroupProduct = (variableToCheck: unknown): variableToCheck is ProductBundle => {
  return (
    typeof variableToCheck === "object" &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (variableToCheck as any)?.courseGroups !== "undefined"
  );
};

interface ProductCardProps {
  product: ProductType | Optional<ProductBundle, "courseGroups" | "bundleFields">;
  href: string;
  productType: "course" | "bundle" | "credits" | "privatePracticeTool";
}

const ProductCard: React.FC<ProductCardProps> = ({ product, href, children, productType }) => {
  const { slug, name } = product;
  const courseGroup = isCourseGroupProduct(product) ? product.courseGroups[0] : null;
  const productName = courseGroup?.title || name;

  const image = isProductType(product) ? product.image : null;
  const variations = isVariableProduct(product) ? product.variations : null;

  const startingAtPrice = useMemo(() => {
    if (!variations) return null;

    return Math.min(...mapNodes(variations, variant => Number(variant.price?.substr(1))));
  }, [variations]);

  let ceUnits;
  if (productType === "bundle") {
    ceUnits = (product as ProductBundle).courses.reduce(
      (total, course) => total + course.ceUnits,
      0
    );
  }

  return (
    <div className={styles.product} id={slug || ""}>
      {image?.mediaItemUrl && (
        <div className={styles.productImage}>
          <Image layout="fill" objectFit="cover" src={image.mediaItemUrl} />
        </div>
      )}
      <div className={image?.mediaItemUrl ? styles.productText : styles.productTextSmall}>
        <h2 className={styles.productTitle}>{productName}</h2>
        {children}
      </div>

      <div className={cx(styles.productPrice, productType === "bundle" && styles.productBundle)}>
        {productType === "bundle" && (
          <>
            <CreditHoursBadge ceUnits={ceUnits} />
            <CourseBadge size="sm" type="discount" />
          </>
        )}
        {isVariableProduct(product) && startingAtPrice ? (
          <>
            <div className={styles.productPriceVariation}>Starting At</div>
            {formatUSD(startingAtPrice)}
          </>
        ) : (
          <>
            {productType === "bundle" && "Only"} {formatUSD(product.priceUSD)}
          </>
        )}
        <div className={styles.productButton}>
          <ButtonLink theme="light" href={href}>
            Learn More
          </ButtonLink>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
